import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { LoaderService } from '../services/loader/loader-service';
import { NotificationsService } from '../services/notifications/notifications-service';
import { SKIP_ERROR_TOAST } from './http-context-tokens';
import { environment } from 'src/environments/environment';

@Injectable()
export class HttpRequestsInterceptor implements HttpInterceptor {
    private readonly _apiOrigin = environment.API_ORIGIN;
    private readonly _authOrigin = environment.AUTH_ORIGIN;
    private readonly _azureCoreUrl = environment.AZURE_CORE_URL;
    private readonly _azureMeasurementsUrl = environment.AZURE_MEASUREMENTS_URL;
    private readonly unsubscribe$ = new Subject();
    constructor(
        private _notificationsService: NotificationsService,
        private _loaderService: LoaderService
    ) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler) {
        let url = req.url;
        let fullUrlReq: HttpRequest<unknown> = req.clone();
        const locationId = this.getLocationIdFromUrl();

        if (!fullUrlReq.url.includes('http') && fullUrlReq.url.includes('auth')) {
            fullUrlReq = req.clone({
                headers: req.headers.set('Authorization', `Bearer ${this.getToken()}`),
                url: this._authOrigin + url
            });
        } else if (
            !fullUrlReq.url.includes('http') &&
            !fullUrlReq.url.startsWith('assets') &&
            !fullUrlReq.url.includes('auth')
        ) {
            //console.log('not auth');
            if (url.startsWith('/placeholder')) {
                url = url.replace('/placeholder', '');
            }

            let negotiateUrl = `${this._azureCoreUrl}/${req.url}`;
            if (locationId) {
                const separator = negotiateUrl.includes('?') ? '&' : '?';
                negotiateUrl = `${negotiateUrl}${separator}locationId=${encodeURIComponent(locationId)}`;
            }

            fullUrlReq = req.clone({
                headers: req.headers.set('Authorization', `Bearer ${this.getToken()}`).set('Web', 'true'),
                withCredentials: true,
                // url: this._apiOrigin + url,
                url:
                    req.url === 'negotiate'
                        ? negotiateUrl //&macAddress=C164D1FEFF2C6A3C'
                        : fullUrlReq.url.includes('measurements')
                          ? `${this._azureMeasurementsUrl}${req.url}`
                          : `${this._apiOrigin}${req.url}`
            });
        }

        const toRun = next.handle(fullUrlReq);

        this._loaderService.show();
        return toRun.pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 498) {
                    return this._refreshToken(next, fullUrlReq);
                }
                if (!req.context.get(SKIP_ERROR_TOAST)) {
                    this._notificationsService.displayHttpError(error.error);
                }
                return throwError(() => error);
            }),
            finalize(() => {
                this._loaderService.hide();
            })
        );
    }

    private getLocationIdFromUrl(): string | null {
        if (typeof window === 'undefined') {
            return null;
        }

        const params = new URLSearchParams(window.location.search);
        return params.get('locationId');
    }

    private _refreshToken(next: HttpHandler, req: HttpRequest<unknown>) {
        const refreshReq = req.clone({
            url: this._authOrigin + '/api/user/refresh-token/',
            headers: req.headers.set('Authorization', `Bearer ${this.getRefreshToken()}`).set('Web', 'true'),
            method: 'POST',
            body: {}
        });

        next.handle(refreshReq).subscribe({
            next: (token: any) => {
                if (token?.body?.access_token) {
                    localStorage.setItem('access_token', token.body.access_token);
                    this.unsubscribe$.next(true);
                }
            },
            error: () => {
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                sessionStorage.removeItem('access_token');
                sessionStorage.removeItem('refresh_token');
                // navigate to login
                location.href = '/login';
            }
        });

        return this.unsubscribe$.pipe(
            switchMap(() => {
                const newReq = req.clone({
                    headers: req.headers.set('Authorization', `Bearer ${this.getToken()}`)
                });
                return next.handle(newReq);
            })
        );
    }

    getToken() {
        return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    }

    getRefreshToken() {
        return localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');
    }
}
